"use client";

import React, { useState } from "react";
import { ChevronDown, HelpCircle, ArrowRight } from "lucide-react";

/* ─────────────────────────────────────────
   DATA
───────────────────────────────────────── */

const FAQS = [
  {
    question: "Mua guitar cũ ở TPHCM có an toàn không?",
    answer:
      "Hoàn toàn an toàn nếu bạn mua tại cửa hàng uy tín. Tại Guitar Sài Gòn, mỗi cây guitar cũ đều được kiểm tra kỹ thuật toàn diện trước khi bày bán — bao gồm cần đàn, action, phím, thùng và âm thanh thực tế. Chúng tôi cung cấp báo cáo tình trạng minh bạch và cam kết hoàn tiền nếu phát sinh lỗi ẩn trong 7 ngày đầu.",
  },
  {
    question: "Guitar đã qua sử dụng có bảo hành không?",
    answer:
      "Có. Guitar cũ tại Guitar Sài Gòn được bảo hành 3 tháng đối với các lỗi kỹ thuật phát sinh sau mua — bao gồm lỗi cần đàn, cong phím hoặc tách keo thùng. Điều kiện bảo hành: giữ nguyên tình trạng ban đầu, không va đập mạnh hoặc ngâm ẩm sau khi mua.",
  },
  {
    question: "Có khóa học guitar cho người mới ở Quận 12 không?",
    answer:
      "Có. Guitar Sài Gòn tổ chức khóa học guitar 1 kèm 1 tại Quận 12, TPHCM — phù hợp từ người mới hoàn toàn đến học viên muốn nâng cao kỹ thuật. Lịch học linh hoạt theo giờ rảnh của bạn. Buổi học thử đầu tiên hoàn toàn miễn phí, không cần cam kết trước.",
  },
  {
    question: "Nên mua guitar ở Sài Gòn ở đâu?",
    answer:
      "Guitar Sài Gòn là địa chỉ được nhiều học viên và người chơi guitar tại TPHCM tin tưởng. Chúng tôi cung cấp cả guitar mới lẫn guitar cũ đã kiểm tra kỹ thuật, với mức giá minh bạch và hỗ trợ tư vấn tận tâm. Cửa hàng tọa lạc tại Quận 12, dễ dàng tiếp cận từ Gò Vấp, Hóc Môn và Bình Dương.",
  },
  {
    question: "Chi phí học guitar 1 kèm 1 tại Guitar Sài Gòn là bao nhiêu?",
    answer:
      "Học phí được tư vấn cụ thể dựa theo trình độ và số buổi đăng ký. Chúng tôi có các gói học linh hoạt từ 4 buổi đến trọn lộ trình 12 tuần. Liên hệ trực tiếp để nhận báo giá chính xác và được tư vấn chọn gói phù hợp.",
  },
  {
    question: "Tôi có thể mang guitar cũ đến để định giá thu mua không?",
    answer:
      "Hoàn toàn được. Bạn chỉ cần mang đàn đến cửa hàng tại Quận 12 — kỹ thuật viên sẽ kiểm tra và định giá trong vòng 15–30 phút. Không cần hẹn trước. Nếu đồng ý giá, thanh toán ngay tại chỗ bằng tiền mặt hoặc chuyển khoản.",
  },
];

/* ─────────────────────────────────────────
   COMPONENT
───────────────────────────────────────── */

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section
      aria-labelledby="faq-heading"
      itemScope
      itemType="https://schema.org/FAQPage"
      style={{
        backgroundColor: "var(--background)",
        color: "var(--foreground)",
        fontFamily: "var(--font-roboto)",
      }}
      className="w-full py-20 px-4 sm:px-6 lg:px-8"
    >
      <div className="max-w-7xl mx-auto">

        {/* ── HEADER ── */}
        <header className="text-center mb-16">

          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold mb-6 border"
            style={{
              backgroundColor: "rgba(200,169,110,0.08)",
              borderColor: "rgba(200,169,110,0.35)",
              color: "var(--brand-primary)",
              fontFamily: "var(--font-montserrat)",
            }}
          >
            <HelpCircle className="w-4 h-4" />
            Giải đáp thắc mắc
          </div>

          {/* H2 */}
          <h2
            id="faq-heading"
            className="text-3xl sm:text-4xl md:text-5xl font-black leading-tight mb-5 tracking-tight"
            style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
          >
            Câu Hỏi Thường Gặp Khi{" "}
            <span
              className="relative inline-block"
              style={{ color: "var(--brand-primary)" }}
            >
              Mua Guitar
              <span
                className="absolute left-0 -bottom-1 w-full h-0.5 rounded-full"
                style={{ backgroundColor: "var(--brand-primary)", opacity: 0.4 }}
              />
            </span>{" "}
            Ở TPHCM
          </h2>

          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
            style={{ color: "var(--muted-foreground)" }}
          >
            Tổng hợp những câu hỏi phổ biến nhất từ khách hàng về{" "}
            <strong style={{ color: "var(--foreground)", fontWeight: 600 }}>
              mua guitar cũ
            </strong>
            ,{" "}
            <strong style={{ color: "var(--foreground)", fontWeight: 600 }}>
              học guitar tại Quận 12
            </strong>{" "}
            và dịch vụ tại Guitar Sài Gòn.
          </p>

          {/* Divider */}
          <div className="mt-8 flex items-center justify-center gap-3">
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: "var(--brand-primary)" }} />
            <div className="h-px w-16 rounded-full" style={{ backgroundColor: "rgba(200,169,110,0.2)" }} />
          </div>
        </header>

        {/* ── FAQ GRID ── */}
        <div className="lg:grid lg:grid-cols-[1fr_360px] lg:gap-14 items-start">

          {/* LEFT: Accordion */}
          <div className="space-y-3 mb-10 lg:mb-0">
            {FAQS.map((faq, i) => {
              const isOpen = openIndex === i;
              return (
                <div
                  key={i}
                  className="rounded-2xl border overflow-hidden transition-all duration-200"
                  style={{
                    borderColor: isOpen ? "rgba(200,169,110,0.45)" : "var(--border)",
                    backgroundColor: "var(--card)",
                    boxShadow: isOpen ? "0 4px 24px rgba(200,169,110,0.08)" : "none",
                  }}
                  itemScope
                  itemProp="mainEntity"
                  itemType="https://schema.org/Question"
                >
                  {/* Trigger */}
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    className="w-full flex items-center gap-4 px-6 py-5 text-left"
                    aria-expanded={isOpen}
                  >
                    {/* Number */}
                    <div
                      className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black transition-all duration-200"
                      style={{
                        fontFamily: "var(--font-montserrat)",
                        backgroundColor: isOpen ? "var(--brand-primary)" : "rgba(200,169,110,0.1)",
                        color: isOpen ? "var(--brand-primary-foreground)" : "var(--brand-primary)",
                        border: isOpen ? "none" : "1.5px solid rgba(200,169,110,0.25)",
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>

                    {/* Question */}
                    <h3
                      className="flex-1 font-bold text-sm sm:text-base leading-snug text-left"
                      style={{
                        fontFamily: "var(--font-montserrat)",
                        color: isOpen ? "var(--foreground)" : "var(--foreground)",
                      }}
                      itemProp="name"
                    >
                      {faq.question}
                    </h3>

                    {/* Chevron */}
                    <ChevronDown
                      className="shrink-0 w-5 h-5 transition-transform duration-300"
                      style={{
                        color: "var(--brand-primary)",
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </button>

                  {/* Answer */}
                  {isOpen && (
                    <div
                      className="px-6 pb-6"
                      style={{ borderTop: "1px solid rgba(200,169,110,0.12)" }}
                      itemScope
                      itemProp="acceptedAnswer"
                      itemType="https://schema.org/Answer"
                    >
                      <div className="pt-4 flex gap-4">
                        <div
                          className="shrink-0 w-0.5 rounded-full self-stretch"
                          style={{ backgroundColor: "rgba(200,169,110,0.35)" }}
                        />
                        <p
                          className="text-sm leading-relaxed"
                          style={{ color: "var(--muted-foreground)" }}
                          itemProp="text"
                        >
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* RIGHT: Sticky CTA card */}
          <div className="lg:sticky lg:top-8">
            <div
              className="rounded-3xl p-7 border"
              style={{
                backgroundColor: "var(--card)",
                borderColor: "rgba(200,169,110,0.3)",
                boxShadow: "0 0 40px rgba(200,169,110,0.06)",
              }}
            >
              {/* Gold top bar */}
              <div
                className="h-1 w-10 rounded-full mb-6"
                style={{ backgroundColor: "var(--brand-primary)" }}
              />

              <p
                className="text-xs font-bold uppercase tracking-widest mb-3"
                style={{ color: "var(--brand-primary)", fontFamily: "var(--font-montserrat)" }}
              >
                Vẫn còn thắc mắc?
              </p>

              <h3
                className="text-xl font-black mb-3 leading-snug"
                style={{ fontFamily: "var(--font-montserrat)", color: "var(--foreground)" }}
              >
                Liên hệ trực tiếp với mình
              </h3>

              <p
                className="text-sm leading-relaxed mb-7"
                style={{ color: "var(--muted-foreground)" }}
              >
                Đội ngũ Guitar Sài Gòn sẵn sàng tư vấn miễn phí — từ chọn đàn, kiểm tra guitar cũ đến lộ trình học phù hợp.
              </p>



              {/* Primary CTA */}
              <a
                href="tel:+84325805893"
                className="group flex items-center justify-center gap-2 w-full py-4 rounded-xl font-bold text-sm transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
                style={{
                  backgroundColor: "var(--brand-primary)",
                  color: "var(--brand-primary-foreground)",
                  fontFamily: "var(--font-montserrat)",
                  boxShadow: "0 4px 20px rgba(200,169,110,0.28)",
                }}
              >
                Gọi Ngay – Tư Vấn Miễn Phí
              </a>

              {/* Hours note */}
              <p
                className="text-xs text-center mt-4"
                style={{ color: "var(--muted-foreground)" }}
              >
                Mở cửa 8:00 – 20:00 · Thứ 2 – Chủ nhật
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default FAQSection;